import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    IconButton,
    Tooltip,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {
    SupervisorAccount as ManagerIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    CalendarToday as CalendarIcon,
    Lock as LockIcon
} from "@mui/icons-material";
import moment from "moment";
import {addOwner, deleteOwner, editOwner, resetOwnerPassword} from "../../services/system/CompanyService";
import {useState} from "react";
import AddOwnerDialog from "./AddOwnerDialog";
import ChangePasswordDialog from "../client/ChangePasswordDialog";
import {useMessageContext} from "../../contexts/MessageContext";
import {getErrorMessage} from "../../enums/ErrorMessages";
import {formatPhoneForDisplay} from "../shared/PhoneInput";

const CompanyManagersSection = ({compantId, companyOwners, setLoadCompany}) => {
    const {notificationHandler} = useMessageContext();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleDeleteOwner = (id) => {
        deleteOwner(id)
            .then(r => {
                setLoadCompany(prv => !prv)
            })
            .then(() => notificationHandler.success("מנהל חברה נמחק בהצלחה"))
            .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)))
    }

    const [changePasswordOwner, setChangePasswordOwner] = useState(null);

    const handleChangePassword = (password) => {
        resetOwnerPassword(changePasswordOwner.id, password)
            .then(() => { setChangePasswordOwner(null); notificationHandler.success("סיסמה שונתה בהצלחה"); })
            .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)));
    };

    const [openEditOwner, setOpenEditOwner] = useState({
        open: false,
        manager: null
    });

    const closeEditOwner = () => {
        setOpenEditOwner({
            open: false,
            manager: null
        })
    }

    const handleSaveOwner = (manager) => {
        delete manager["secondPassword"];
        if (manager.id) {
            editOwner(manager)
                .then(r => {
                    setLoadCompany(prv => !prv)
                })
                .then(() => notificationHandler.success("מנהל חברה נשמר בהצלחה"))
                .then(closeEditOwner)
                .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)))
        } else {
            addOwner(manager)
                .then(r => {
                    setLoadCompany(prv => !prv)
                })
                .then(closeEditOwner)
                .then(() => notificationHandler.success("מנהל חברה עודכן בהצלחה"))
                .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)))
        }
    }

    const addManger = () => {
        setOpenEditOwner({
            open: true,
            manager: {}
        });
    }

    const updateOwner = (manager) => {
        setOpenEditOwner({
            open: true,
            manager: manager,
        })
    }

    return (
        <Paper
            sx={{
                backgroundColor: "#FCFCFD",
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                direction: "rtl"
            }}
            elevation={0}
        >
            <ChangePasswordDialog
                isOpen={!!changePasswordOwner}
                close={() => setChangePasswordOwner(null)}
                onSubmit={handleChangePassword}
            />

            {openEditOwner.manager && (
                <AddOwnerDialog
                    owner={openEditOwner.manager}
                    open={openEditOwner.open}
                    companyId={compantId}
                    close={closeEditOwner}
                    handleSubmit={handleSaveOwner}
                />
            )}

            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: { xs: "12px 16px", md: "16px 20px" },
                    backgroundColor: "#F8FAFC",
                    borderBottom: "1px solid #E2E8F0"
                }}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                    <ManagerIcon sx={{ color: "#64748B", fontSize: { xs: "18px", md: "20px" } }} />
                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: { xs: "14px", md: "16px" },
                            color: "#1E293B",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}
                    >
                        מנהלים
                    </Typography>
                </Box>

                <Button
                    onClick={addManger}
                    variant="contained"
                    startIcon={<AddIcon sx={{ fontSize: { xs: "16px", md: "20px" } }} />}
                    sx={{
                        backgroundColor: "#1E293B",
                        color: "#FFFFFF",
                        fontSize: { xs: "12px", md: "13px" },
                        padding: { xs: "6px 12px", md: "8px 16px" },
                        "&:hover": {
                            backgroundColor: "#334155"
                        },
                        "& .MuiButton-startIcon": {
                            marginRight: { xs: "-2px", md: "-4px" },
                            marginLeft: { xs: "4px", md: "8px" }
                        }
                    }}
                    size="small"
                >
                    {isMobile ? "הוסף" : "הוסף מנהל"}
                </Button>
            </Box>

            {/* Mobile Card Layout */}
            {isMobile ? (
                <Box sx={{ padding: "12px" }}>
                    {companyOwners.length === 0 ? (
                        <Typography sx={{ textAlign: "center", color: "#64748B", padding: "20px" }}>
                            אין מנהלים
                        </Typography>
                    ) : (
                        companyOwners.map((manager, i) => (
                            <Box
                                key={i}
                                sx={{
                                    backgroundColor: "#FFFFFF",
                                    border: "1px solid #E2E8F0",
                                    borderRadius: "8px",
                                    padding: "12px 14px",
                                    marginBottom: "8px",
                                    "&:last-child": { marginBottom: 0 }
                                }}
                            >
                                {/* Top Row: Name + Actions */}
                                <Box sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "10px"
                                }}>
                                    <Typography sx={{
                                        fontSize: "14px",
                                        fontWeight: 600,
                                        color: "#1E293B"
                                    }}>
                                        {manager.name}
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 0.5 }}>
                                        <IconButton
                                            onClick={() => updateOwner(manager)}
                                            sx={{
                                                color: "#64748B",
                                                padding: "6px",
                                                "&:hover": {
                                                    backgroundColor: "#E2E8F0",
                                                    color: "#475569"
                                                }
                                            }}
                                            size="small"
                                        >
                                            <EditIcon sx={{ fontSize: "18px" }} />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => setChangePasswordOwner(manager)}
                                            sx={{
                                                color: "#64748B",
                                                padding: "6px",
                                                "&:hover": {
                                                    backgroundColor: "#E2E8F0",
                                                    color: "#475569"
                                                }
                                            }}
                                            size="small"
                                        >
                                            <LockIcon sx={{ fontSize: "18px" }} />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleDeleteOwner(manager.id)}
                                            sx={{
                                                color: "#EF4444",
                                                padding: "6px",
                                                "&:hover": {
                                                    backgroundColor: "#FEF2F2",
                                                    color: "#DC2626"
                                                }
                                            }}
                                            size="small"
                                        >
                                            <DeleteIcon sx={{ fontSize: "18px" }} />
                                        </IconButton>
                                    </Box>
                                </Box>

                                {/* Info Grid */}
                                <Box sx={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "8px",
                                    backgroundColor: "#F8FAFC",
                                    borderRadius: "6px",
                                    padding: "10px"
                                }}>
                                    {/* Phone */}
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <PhoneIcon sx={{ fontSize: "14px", color: "#64748B" }} />
                                        <Typography sx={{
                                            fontSize: "12px",
                                            color: "#64748B",
                                            direction: "ltr"
                                        }}>
                                            {formatPhoneForDisplay(manager.phoneNumber) || "אין מידע"}
                                        </Typography>
                                    </Box>

                                    {/* Date */}
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <CalendarIcon sx={{ fontSize: "14px", color: "#64748B" }} />
                                        <Typography sx={{ fontSize: "12px", color: "#64748B" }}>
                                            {moment(manager.createDate).format("DD/MM/YY")}
                                        </Typography>
                                    </Box>

                                    {/* Email - Full Width */}
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, gridColumn: "1 / -1" }}>
                                        <EmailIcon sx={{ fontSize: "14px", color: "#64748B" }} />
                                        <Typography sx={{
                                            fontSize: "12px",
                                            color: "#64748B",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap"
                                        }}>
                                            {manager.email || "אין מידע"}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))
                    )}
                </Box>
            ) : (
                /* Desktop Table */
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                                <TableCell
                                    align="right"
                                    sx={{
                                        borderBottom: "1px solid #E2E8F0",
                                        padding: "12px 16px",
                                        color: "#64748B",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px"
                                    }}
                                >
                                    שם מלא
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        borderBottom: "1px solid #E2E8F0",
                                        padding: "12px 16px",
                                        color: "#64748B",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px"
                                    }}
                                >
                                    טלפון
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        borderBottom: "1px solid #E2E8F0",
                                        padding: "12px 16px",
                                        color: "#64748B",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px"
                                    }}
                                >
                                    מייל
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        borderBottom: "1px solid #E2E8F0",
                                        padding: "12px 16px",
                                        color: "#64748B",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px"
                                    }}
                                >
                                    תאריך יצירה
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        borderBottom: "1px solid #E2E8F0",
                                        padding: "12px 16px",
                                        color: "#64748B",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                        width: "100px"
                                    }}
                                >
                                    פעולות
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {companyOwners.map((manager, i) => {
                                return (
                                    <TableRow
                                        key={i}
                                        sx={{
                                            "&:hover": {
                                                backgroundColor: "#F8FAFC"
                                            },
                                            borderBottom: "1px solid #E2E8F0"
                                        }}
                                    >
                                        <TableCell
                                            align="right"
                                            sx={{
                                                padding: "12px 16px",
                                                borderBottom: "none"
                                            }}
                                        >
                                            <Typography sx={{
                                                fontSize: "14px",
                                                fontWeight: 600,
                                                color: "#1E293B"
                                            }}>
                                                {manager.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                padding: "12px 16px",
                                                borderBottom: "none"
                                            }}
                                        >
                                            <Typography sx={{
                                                fontSize: "13px",
                                                color: "#64748B",
                                                direction: "ltr"
                                            }}>
                                                {formatPhoneForDisplay(manager.phoneNumber) || "אין מידע"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                padding: "12px 16px",
                                                borderBottom: "none"
                                            }}
                                        >
                                            <Typography sx={{
                                                fontSize: "13px",
                                                color: "#64748B",
                                                wordBreak: "break-all"
                                            }}>
                                                {manager.email || "אין מידע"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                padding: "12px 16px",
                                                borderBottom: "none"
                                            }}
                                        >
                                            <Typography sx={{
                                                fontSize: "13px",
                                                color: "#64748B"
                                            }}>
                                                {moment(manager.createDate).format("DD/MM/YY")}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                padding: "12px 16px",
                                                borderBottom: "none"
                                            }}
                                        >
                                            <Box sx={{display: "flex", justifyContent: "center", gap: 0.5}}>
                                                <Tooltip title="עריכת מנהל">
                                                    <IconButton
                                                        onClick={() => updateOwner(manager)}
                                                        sx={{
                                                            color: "#64748B",
                                                            "&:hover": {
                                                                backgroundColor: "#E2E8F0",
                                                                color: "#475569"
                                                            }
                                                        }}
                                                        size="small"
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="שינוי סיסמה">
                                                    <IconButton
                                                        onClick={() => setChangePasswordOwner(manager)}
                                                        sx={{
                                                            color: "#64748B",
                                                            "&:hover": {
                                                                backgroundColor: "#E2E8F0",
                                                                color: "#475569"
                                                            }
                                                        }}
                                                        size="small"
                                                    >
                                                        <LockIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="מחיקת מנהל">
                                                    <IconButton
                                                        onClick={() => handleDeleteOwner(manager.id)}
                                                        sx={{
                                                            color: "#EF4444",
                                                            "&:hover": {
                                                                backgroundColor: "#FEF2F2",
                                                                color: "#DC2626"
                                                            }
                                                        }}
                                                        size="small"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Paper>
    );
};

export default CompanyManagersSection;